import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { usePermissions } from '../hooks/usePermissions';

const C = {
  primary: '#2E7D32', primaryDark: '#1B5E20',
  border: '#E0E0E0', light: '#F4F6F9',
  green50: '#F1F8F2', green100: '#E8F5E9',
  textDark: '#212121', textMid: '#616161', textLight: '#9E9E9E',
};

const S = {
  page: { minHeight: '100vh', background: C.light },
  topbar: { height: 52, background: '#fff', borderBottom: `1px solid ${C.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', position: 'sticky', top: 0, zIndex: 100, flexShrink: 0 },
  breadcrumb: { fontSize: 13, color: C.textLight, display: 'flex', alignItems: 'center', gap: 6 },
  breadcrumbActive: { color: C.textDark, fontWeight: 600 },
  container: { maxWidth: 920, margin: '20px auto', padding: '0 20px', paddingBottom: 40 },
  carte: { background: '#fff', borderRadius: 12, border: `0.5px solid ${C.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 12, overflow: 'hidden' },
  sectionHeader: { background: C.primary, color: '#fff', padding: '10px 16px',
    fontSize: 13, fontWeight: 700 },
  carteBody: { padding: '14px 16px' },
  grille2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  grille4: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 },
  label: { display: 'block', fontSize: 12, color: C.textMid, marginBottom: 4, fontWeight: 600 },
  input: { width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`,
    borderRadius: 7, fontSize: 12, boxSizing: 'border-box', outline: 'none' },
  inputNet: { width: '100%', padding: '7px 10px', border: '1px solid #A5D6A7',
    borderRadius: 7, fontSize: 12, background: C.green100, fontWeight: 700,
    color: C.primaryDark, boxSizing: 'border-box' },
  soustitre: { fontSize: 11, color: C.textLight, marginTop: 3 },
  alerte: (t) => ({
    padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
    background: t === 'succes' ? C.green100 : '#FFEBEE',
    color: t === 'succes' ? C.primaryDark : '#C62828',
    border: `1px solid ${t === 'succes' ? '#A5D6A7' : '#EF9A9A'}`,
  }),
  btnValider: { width: '100%', padding: 14, background: C.primary, color: 'white',
    border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 8 },
  subBox: (color, bg, border) => ({
    marginTop: 12, padding: '12px 14px', background: bg, borderRadius: 8,
    border: `1px ${border} ${color}`,
  }),
  subBoxTitle: (color) => ({ fontWeight: 700, color, marginBottom: 8, fontSize: 12 }),
};

export default function SaisiePage() {
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const { peut, loading: loadingPerms } = usePermissions();
  const [abattoirs, setAbattoirs] = useState([]);
  const [message, setMessage] = useState({ type: '', texte: '' });
  const [chargement, setChargement] = useState(false);

  const [form, setForm] = useState({
    abattoir_id: '', date_saisie: new Date().toISOString().split('T')[0],
    bovins_brut: 0, poids_bovins: 0,
    camelins: 0, poids_camelins: 0,
    sorties_bovins: 0, vf_bovins: 0,
    ovins: 0, poids_ovins: 0,
    sorties_ovins: 0, vf_ovins: 0,
    caprins: 0, poids_caprins: 0,
    sorties_caprins: 0, vf_caprins: 0,
    porcins: 0, poids_porcins: 0,
    sorties_porcins: 0, vf_porcins: 0,
    observations: ''
  });

  useEffect(() => {
    api.get('/abattoirs').then(r => setAbattoirs(r.data));
  }, []);

  const champ = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Calculs nets
  const bovins_nets_nbr = Math.max(0, (parseInt(form.bovins_brut)||0) - (parseInt(form.camelins)||0) - (parseInt(form.sorties_bovins)||0));
  const bovins_nets_poids = Math.max(0, (parseFloat(form.poids_bovins)||0) - (parseFloat(form.poids_camelins)||0) - (parseFloat(form.vf_bovins)||0));
  const ovins_nets_nbr = Math.max(0, (parseInt(form.ovins)||0) - (parseInt(form.sorties_ovins)||0));
  const ovins_nets_poids = Math.max(0, (parseFloat(form.poids_ovins)||0) - (parseFloat(form.vf_ovins)||0));
  const caprins_nets_nbr = Math.max(0, (parseInt(form.caprins)||0) - (parseInt(form.sorties_caprins)||0));
  const caprins_nets_poids = Math.max(0, (parseFloat(form.poids_caprins)||0) - (parseFloat(form.vf_caprins)||0));
  const porcins_nets_nbr = Math.max(0, (parseInt(form.porcins)||0) - (parseInt(form.sorties_porcins)||0));
  const porcins_nets_poids = Math.max(0, (parseFloat(form.poids_porcins)||0) - (parseFloat(form.vf_porcins)||0));

  const soumettre = async (e) => {
    e.preventDefault();
    if (!form.abattoir_id) return setMessage({ type: 'erreur', texte: 'Veuillez choisir un abattoir.' });
    setChargement(true);
    try {
      await api.post('/saisies', form);
      setMessage({ type: 'succes', texte: 'Saisie enregistrée avec succès !' });
      setTimeout(() => navigate('/saisies'), 2000);
    } catch (err) {
      setMessage({ type: 'erreur', texte: err.response?.data?.message || 'Erreur serveur.' });
    } finally {
      setChargement(false);
    }
  };

  const peutEcrire = peut('abattage', 'ecrire');
  const lectureSeule = !loadingPerms && !peutEcrire;

  const inputStyle = (base) => lectureSeule
    ? { ...base, background: '#F5F5F5', color: '#9E9E9E', cursor: 'not-allowed', pointerEvents: 'none' }
    : base;

  return (
    <div style={S.page}>
      {/* Topbar */}
      <div style={S.topbar}>
        <div style={S.breadcrumb}>
          <span>Production</span>
          <span style={{ color: C.border }}>›</span>
          <span style={S.breadcrumbActive}>Saisie d'abattage</span>
        </div>
        <button style={{ background: 'transparent', border: 'none', color: C.textMid, fontSize: 13, cursor: 'pointer' }}
          onClick={() => navigate('/saisies')}>← Retour à la liste</button>
      </div>

      <div style={S.container}>
        {lectureSeule && (
          <div style={{ background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: 8,
            padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#1565C0',
            display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>👁️</span>
            <span><strong>Mode lecture seule</strong> — Contactez un administrateur pour les droits d'écriture.</span>
          </div>
        )}

        {message.texte && <div style={S.alerte(message.type)}>{message.texte}</div>}

        <form onSubmit={lectureSeule ? e => e.preventDefault() : soumettre}>
          {/* Informations générales */}
          <div style={S.carte}>
            <div style={S.sectionHeader}>📍 Informations générales</div>
            <div style={S.carteBody}>
              <div style={S.grille2}>
                <div>
                  <label style={S.label}>Abattoir</label>
                  <select style={inputStyle(S.input)} name="abattoir_id" value={form.abattoir_id}
                    onChange={lectureSeule ? undefined : champ} disabled={lectureSeule}>
                    <option value="">-- Choisir --</option>
                    {abattoirs.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Date</label>
                  <input style={inputStyle(S.input)} type="date" name="date_saisie"
                    value={form.date_saisie} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} />
                </div>
              </div>
            </div>
          </div>

          {/* Bovins */}
          <div style={S.carte}>
            <div style={S.sectionHeader}>🐄 BOVINS</div>
            <div style={S.carteBody}>
              <div style={S.grille4}>
                <div>
                  <label style={S.label}>Nombre brut</label>
                  <input style={inputStyle(S.input)} type="number" min="0" name="bovins_brut"
                    value={form.bovins_brut} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} />
                </div>
                <div>
                  <label style={S.label}>Poids total (kg)</label>
                  <input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="poids_bovins"
                    value={form.poids_bovins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} />
                </div>
                <div>
                  <label style={S.label}>Sorties (têtes)</label>
                  <input style={inputStyle(S.input)} type="number" min="0" name="sorties_bovins"
                    value={form.sorties_bovins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} />
                  <div style={S.soustitre}>Bêtes écartées</div>
                </div>
                <div>
                  <label style={S.label}>Viande foraine (kg)</label>
                  <input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="vf_bovins"
                    value={form.vf_bovins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} />
                  <div style={S.soustitre}>Viande extérieure</div>
                </div>
              </div>

              {/* Camelins */}
              <div style={S.subBox('#FFCA28', '#FFFDE7', 'dashed')}>
                <div style={S.subBoxTitle('#F57F17')}>🐪 CAMELINS (inclus dans les bovins)</div>
                <div style={S.grille2}>
                  <div>
                    <label style={S.label}>Nombre camelins</label>
                    <input style={inputStyle(S.input)} type="number" min="0" name="camelins"
                      value={form.camelins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} />
                  </div>
                  <div>
                    <label style={S.label}>Poids camelins (kg)</label>
                    <input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="poids_camelins"
                      value={form.poids_camelins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} />
                  </div>
                </div>
              </div>

              {/* Bovins nets */}
              <div style={S.subBox('#A5D6A7', C.green100, 'solid')}>
                <div style={S.subBoxTitle(C.primaryDark)}>✅ BOVINS NETS (calculé automatiquement)</div>
                <div style={S.grille2}>
                  <div>
                    <label style={S.label}>Nombre net = Brut − Camelins − Sorties</label>
                    <input style={S.inputNet} type="number" value={bovins_nets_nbr} readOnly />
                  </div>
                  <div>
                    <label style={S.label}>Poids net = Total − Camelins − V.Foraine</label>
                    <input style={S.inputNet} type="number" value={bovins_nets_poids} readOnly />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ovins */}
          <div style={S.carte}>
            <div style={S.sectionHeader}>🐑 OVINS</div>
            <div style={S.carteBody}>
              <div style={S.grille4}>
                <div><label style={S.label}>Nombre brut</label><input style={inputStyle(S.input)} type="number" min="0" name="ovins" value={form.ovins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Poids total (kg)</label><input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="poids_ovins" value={form.poids_ovins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Sorties (têtes)</label><input style={inputStyle(S.input)} type="number" min="0" name="sorties_ovins" value={form.sorties_ovins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Viande foraine (kg)</label><input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="vf_ovins" value={form.vf_ovins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
              </div>
              <div style={S.subBox('#A5D6A7', C.green100, 'solid')}>
                <div style={S.subBoxTitle(C.primaryDark)}>✅ OVINS NETS</div>
                <div style={S.grille2}>
                  <div><label style={S.label}>Nombre net</label><input style={S.inputNet} type="number" value={ovins_nets_nbr} readOnly /></div>
                  <div><label style={S.label}>Poids net (kg)</label><input style={S.inputNet} type="number" value={ovins_nets_poids} readOnly /></div>
                </div>
              </div>
            </div>
          </div>

          {/* Caprins */}
          <div style={S.carte}>
            <div style={S.sectionHeader}>🐐 CAPRINS</div>
            <div style={S.carteBody}>
              <div style={S.grille4}>
                <div><label style={S.label}>Nombre brut</label><input style={inputStyle(S.input)} type="number" min="0" name="caprins" value={form.caprins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Poids total (kg)</label><input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="poids_caprins" value={form.poids_caprins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Sorties (têtes)</label><input style={inputStyle(S.input)} type="number" min="0" name="sorties_caprins" value={form.sorties_caprins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Viande foraine (kg)</label><input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="vf_caprins" value={form.vf_caprins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
              </div>
              <div style={S.subBox('#A5D6A7', C.green100, 'solid')}>
                <div style={S.subBoxTitle(C.primaryDark)}>✅ CAPRINS NETS</div>
                <div style={S.grille2}>
                  <div><label style={S.label}>Nombre net</label><input style={S.inputNet} type="number" value={caprins_nets_nbr} readOnly /></div>
                  <div><label style={S.label}>Poids net (kg)</label><input style={S.inputNet} type="number" value={caprins_nets_poids} readOnly /></div>
                </div>
              </div>
            </div>
          </div>

          {/* Porcins */}
          <div style={S.carte}>
            <div style={S.sectionHeader}>🐖 PORCINS</div>
            <div style={S.carteBody}>
              <div style={S.grille4}>
                <div><label style={S.label}>Nombre brut</label><input style={inputStyle(S.input)} type="number" min="0" name="porcins" value={form.porcins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Poids total (kg)</label><input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="poids_porcins" value={form.poids_porcins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Sorties (têtes)</label><input style={inputStyle(S.input)} type="number" min="0" name="sorties_porcins" value={form.sorties_porcins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
                <div><label style={S.label}>Viande foraine (kg)</label><input style={inputStyle(S.input)} type="number" min="0" step="0.01" name="vf_porcins" value={form.vf_porcins} onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule} /></div>
              </div>
              <div style={S.subBox('#A5D6A7', C.green100, 'solid')}>
                <div style={S.subBoxTitle(C.primaryDark)}>✅ PORCINS NETS</div>
                <div style={S.grille2}>
                  <div><label style={S.label}>Nombre net</label><input style={S.inputNet} type="number" value={porcins_nets_nbr} readOnly /></div>
                  <div><label style={S.label}>Poids net (kg)</label><input style={S.inputNet} type="number" value={porcins_nets_poids} readOnly /></div>
                </div>
              </div>
            </div>
          </div>

          {/* Observations */}
          <div style={S.carte}>
            <div style={S.sectionHeader}>📝 Observations</div>
            <div style={S.carteBody}>
              <textarea style={inputStyle({ ...S.input, height: 90, resize: lectureSeule ? 'none' : 'vertical' })}
                name="observations" value={form.observations}
                onChange={lectureSeule ? undefined : champ} readOnly={lectureSeule}
                placeholder="Remarques éventuelles..." />
            </div>
          </div>

          {!lectureSeule && (
            <button type="submit" style={S.btnValider} disabled={chargement}>
              {chargement ? '⏳ Enregistrement...' : '💾 ENREGISTRER LA SAISIE'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
